import { Component, inject, OnInit } from '@angular/core';
import { GridModule, PagerModule } from '@syncfusion/ej2-angular-grids';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { MatIconRegistry } from '@angular/material/icon';

import { I18nService } from './core/i18n.service';
import * as itIT from '../translations/it-IT.json';
import * as enGB from '../translations/en-GB.json';

import { environment } from '../../../commondata/environments/environment';
import { ShellMessageService } from './services/shell-message.service';
import { LoaderService } from './shared/loader/loader.service';
import { LoaderComponent } from './shared/loader/loader.component';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { FaviconService } from './services/favicon/favicon.service';
import { filter, map, mergeMap } from 'rxjs';

import { MatomoTracker } from 'ngx-matomo-client';

@Component({
    selector: 'app-root',
    imports: [GridModule, PagerModule, RouterOutlet, LoaderComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'shell';

  constructor(
    private i18nService: I18nService,
    private translateService: TranslateService,
    private matIconReg: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private router: Router,
    private faviconService: FaviconService,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private tracker: MatomoTracker
  ) { }

  private shellMessageService = inject(ShellMessageService);
  public loaderService = inject(LoaderService);

  printSMXLogo(): void {
    console.log(
      `%c
     ███████╗███╗   ███╗██╗  ██╗
     ██╔════╝████╗ ████║╚██╗██╔╝
     ███████╗██╔████╔██║ ╚███╔╝ 
     ╚════██║██║╚██╔╝██║ ██╔██╗ 
     ███████║██║ ╚═╝ ██║██╔╝ ██╗
     ╚══════╝╚═╝     ╚═╝╚═╝  ╚═╝
    %c
     ███████╗████████╗██╗   ██╗██████╗ ██╗ ██████╗ 
     ██╔════╝╚══██╔══╝██║   ██║██╔══██╗██║██╔═══██╗
     ███████╗   ██║   ██║   ██║██║  ██║██║██║   ██║
     ╚════██║   ██║   ██║   ██║██║  ██║██║██║   ██║
     ███████║   ██║   ╚██████╔╝██████╔╝██║╚██████╔╝
     ╚══════╝   ╚═╝    ╚═════╝ ╚═════╝ ╚═╝ ╚═════╝ 
    `,
      'background: linear-gradient(90deg, #9e3fcd, #ff07c0, #11b4f5); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; font: 400 1em monospace;',
      'color: white; font: 400 1em monospace;',
    );
  }

  ngOnInit(): void {
    this.printSMXLogo();

    // Setup translations
    const translations: any = { 'it-IT': itIT, 'en-GB': enGB };
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages,
      translations,
    );
    environment.supportedLanguages.forEach((element) => {
      this.translateService.getTranslation(element);
    });
    // Set default icon stile
    this.matIconReg.setDefaultFontSetClass('material-symbols-rounded');
    this.shellMessageService.initialize();
    // Register custom icons
    this.matIconReg.addSvgIcon(
      'classroomIcon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/classroom.svg'),
    );
    this.matIconReg.addSvgIcon(
      'latex',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/latex.svg'),
    );
    this.matIconReg.addSvgIcon(
      'math_box',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/math/box.svg'),
    );
    this.matIconReg.addSvgIcon(
      'math_unbox',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/math/un_box.svg'),
    );
    this.matIconReg.addSvgIcon(
      'math_box_off',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/math/box_off.svg'),
    );
    this.matIconReg.addSvgIcon(
      'math_simplify',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/math/simplify.svg'),
    );
    this.matIconReg.addSvgIcon(
      'math_unsimplify',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/math/un_simplify.svg'),
    );
    this.matIconReg.addSvgIcon(
      'math_simplify_off',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/math/simplify_off.svg'),
    );
    this.matIconReg.addSvgIcon(
      'claire',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/claire-icon.svg'),
    );

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((data) => {
        // Set the page title
        if (data['title']) {
          this.titleService.setTitle(data['title']);
        } else {
          this.titleService.setTitle('SMX studio'); // Fallback title
        }

        // Set the favicon based on route data or specific logic
        if (data['favicon']) {
          this.faviconService.setFavicon(data['favicon']);
        } else {
          this.faviconService.setFavicon('assets/favicon.ico'); // Default favicon
        }
      });

    // Initialize Matomo tracker GDPR-compliant
    // Verifichiamo di essere nel browser (e non in Server Side Rendering SSR)
    if (typeof window !== 'undefined') {
      const _paq = ((window as any)._paq = (window as any)._paq || []);
      // Applichiamo i comandi nativi sfruttando il motore asincrono di Matomo
      // _paq.push(['anonymizeIp']);
      _paq.push(['enableHeartBeatTimer', 15]);
      // _paq.push(['discardHashInUrl', true]);
      _paq.push(['disableCookies']);
      _paq.push(['disableBrowserFeatureDetection']);
      _paq.push(['disableCampaignParameters']);
      _paq.push(['setExcludedQueryParams', [
        "account", "accountnum", "address", "address1", "address2", "address3",
        "addressline1", "addressline2", "adres", "adresse", "adresse1", "adresse2",
        "adresse3", "adresse_email", "adresseemail", "adressepostale", "age", "alter",
        "auth", "authpw", "bic", "billingaddress", "billingaddress1", "billingaddress2",
        "calle", "cardnumber", "carte", "cartebancaire", "carteidentite", "cb", "cc",
        "ccc", "cccsc", "cccvc", "cccvv", "ccexpiry", "ccexpmonth", "ccexpyear", "ccname",
        "ccnumber", "cctype", "cell", "cellphone", "city", "civilite", "civilité", "cle",
        "clientid", "clientsecret", "clé", "codepostal", "company", "consumerkey",
        "consumersecret", "contrasenya", "contraseña", "courriel", "cp", "creditcard",
        "creditcardnumber", "cvc", "cvv", "datedenaissance", "dateexpiration", "datenaissance",
        "dateofbirth", "debitcard", "departement", "dirección", "dob", "domain",
        "département", "ebost", "email", "emailaddress", "emailadresse", "entreprise",
        "epos", "epost", "eposta", "exp", "expiration", "familyname", "firma", "firstname",
        "formlogin", "fullname", "gender", "genre", "geschlecht", "gst", "gstnumber",
        "handynummer", "hasło", "heslo", "iban", "ibanaccountnum", "ibanaccountnumber",
        "id", "identifiant", "identifier", "identitenationale", "indirizzo", "kartakredytowa",
        "kennwort", "keyconsumerkey", "keyconsumersecret", "konto", "kontonr", "kontonummer",
        "kredietkaart", "kreditkarte", "kreditkort", "lastname", "login", "mail", "mdp",
        "mobiili", "mobile", "mobilne", "mot_de_passe", "motdepasse", "nachname", "name",
        "nationalite", "nickname", "nom", "nomcomplet", "nomdefamille", "nomfamille",
        "nss", "numero_fiscal", "numerocarte", "numerocarteidentite", "numerocompte",
        "numerodecarte", "numerofiscal", "numeroidentite", "numeromobile", "numeropasseport",
        "numerosecuritesociale", "numerotelephone", "numerotva", "numfiscal", "numsecu",
        "numtva", "osoite", "parole", "pass", "passeport", "passord", "password",
        "passwort", "pasword", "paswort", "paword", "pays", "phone", "pin", "plz",
        "portable", "postalcode", "postcode", "postleitzahl", "prenom", "privatekey",
        "prénom", "publickey", "pw", "pwd", "pword", "pwrd", "questionsecrete", "region",
        "reponsesecrete", "rib", "rue", "secret", "secretclé", "secretq", "secretquestion",
        "securitesociale", "sexe", "shippingaddress", "shippingaddress1", "shippingaddress2",
        "signature", "siren", "siret", "socialsec", "socialsecuritynumber", "societe",
        "socsec", "sokak", "ssn", "steuernummer", "strasse", "street", "surname", "swift",
        "tax", "taxnumber", "tel", "telefon", "telefonnr", "telefonnummer", "telefono",
        "telephone", "titre", "token", "token_auth", "tokenauth", "tva", "téléphone",
        "ulica", "user", "username", "utilisateur", "vat", "vatnumber", "via", "ville",
        "voie", "vorname", "wachtwoord", "wagwoord", "webhooksecret", "website", "zip",
        "zipcode"
      ]]);
      // 2. FORZIAMO il tracciamento della pagina corrente e abilitiamo l'ascolto dei click sui link
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
    }
  }

}
